/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import ImgixWithPrioritizedLoading from 'js/components/ImgixWithPrioritizedLoading';
import { useRouter } from 'js/lib/useRouter';

import { Button, Typography } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
import type { Maybe } from 'bundles/front-page/__generated__/baseContentfulTypes';
import type { MarketingContentFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import CourseraForBusinessCTA from 'bundles/front-page/components/modules/CourseraForBusinessCTA';
import TrackedButton from 'bundles/page/components/TrackedButton';

const redirectUrl = '/onboarding?src=banner,frontpage';

const styles = {
  heroBanner: css`
    background-color: transparent;
    color: var(--cds-color-neutral-primary);
  `,
  bannerContainer: css`
    align-items: center;
    display: flex;
    justify-content: center;
    margin: 0 auto;
    max-width: 1400px;
    position: relative;
    width: 100%;

    @media screen and (max-width: 767px) {
      display: block;
    }
  `,
  textContainer: css`
    margin-left: 100px;
    margin-right: 180px;
    padding: 40px 0 55px;
    position: relative;
    width: 570px;
    z-index: 3;

    @media screen and (max-width: 1225px) {
      margin-right: 100px;
    }

    @media screen and (max-width: 1150px) {
      margin-left: 96px;
      margin-right: 32px;
      max-width: 436px;
      width: 100%;
    }

    @media screen and (max-width: 767px) {
      margin-left: auto;
      margin-right: auto;
      padding: 36px 24px 0;
    }

    @media screen and (max-width: 480px) {
      text-align: center;
    }
  `,
  heroSubheader: () => css`
    margin-top: var(--cds-spacing-300);
    margin-bottom: var(--cds-spacing-300);
  `,
  heroJoinButton: () => css`
    border: 2px solid var(--cds-color-blue-700);
    font-size: 20px;
    height: 72px;
    line-height: 28px;
    margin-right: 10px;
    vertical-align: top;
    width: 238px;

    @media screen and (max-width: 1350px) {
      font-size: inherit;
      height: auto;
      line-height: inherit;
      width: auto;
    }

    @media screen and (max-width: 768px) {
      margin-bottom: 36px;
      margin-right: 0;
      width: 100%;
      height: auto;
    }

    :focus {
      border: 2px solid var(--cds-color-blue-100);
    }
  `,
  imgContainer: css`
    height: 497px;
    width: 459px;
    margin-bottom: 32px;
    margin-right: 24px;
    margin-top: 32px;

    @media screen and (max-width: 1150px) {
      height: 354px;
      width: 328px;
    }

    @media screen and (max-width: 767px) {
      display: none;
    }
  `,
  image: css`
    width: 100%;

    @media screen and (max-width: 1150px) {
      max-height: 354px;
      max-width: 328px;
    }

    @media screen and (max-width: 767px) {
      display: none;
    }
  `,
};

type Props = {
  content?: Maybe<MarketingContentFieldsFragment>;
  entepriseCtaText?: string | null;
  enterpriseCtaLink?: string | null;
};

// Title, description, and CTA are Required.
export const isContentfulContentValid = (
  content?: Maybe<MarketingContentFieldsFragment>
): content is Required<MarketingContentFieldsFragment> =>
  !!(content && content.title && content.description && content.callToActionLabel);

export const HeroBanner = ({ content, entepriseCtaText, enterpriseCtaLink }: Props) => {
  const trackEvent = useTracker();
  const router = useRouter();

  const handleCTAClick = () => {
    trackEvent('click_button', {
      button: { name: 'hero_primary_cta' },
      pageSection: { sectionName: 'hero_banner' },
    });

    const query = { ...(router?.location?.query || {}), authMode: 'signup' };

    router.replace(
      {
        pathname: router?.location?.pathname || '/',
        query,
      },
      // @ts-expect-error
      { pathname: router?.asPath?.split('?')?.[0], query }
    );
  };

  const imgParams = {
    quality: 40,
  };

  if (!isContentfulContentValid(content)) {
    return null;
  }

  const shouldRemoveC4bCta = GrowthDiscoveryExperiments.get('removeC4bCtaFromLohpHero');

  const { title, description, callToActionLabel, visual, visualAlternativeText } = content;

  return (
    <section className="rc-HeroBannerRedesign" css={styles.heroBanner} data-testid="front-page-banner">
      <div css={styles.bannerContainer}>
        <div css={styles.textContainer}>
          <Typography variant="d1semibold" component="h1">
            {title}
          </Typography>
          <Typography variant="h2" component="p" css={styles.heroSubheader}>
            {description}
          </Typography>
          <Button
            component={TrackedButton}
            requireFullyVisible={false}
            withVisibilityTracking={false}
            trackingName="banner_cta"
            data={{ copy: callToActionLabel, href: redirectUrl }}
            css={styles.heroJoinButton}
            onClick={handleCTAClick}
            data-elementtiming="frontpage.banner.cta"
            data-testid="enroll-button"
          >
            {callToActionLabel || ''}
          </Button>
          {entepriseCtaText && !shouldRemoveC4bCta && (
            <CourseraForBusinessCTA label={entepriseCtaText} link={enterpriseCtaLink} useExtraCtaStyling />
          )}
        </div>
        {visual?.url && (
          <div css={styles.imgContainer}>
            <ImgixWithPrioritizedLoading
              css={styles.image}
              imgParams={imgParams}
              alt={visualAlternativeText || ''}
              src={visual?.url}
              maxWidth={459}
              maxHeight={497}
              style={{ marginLeft: 'auto', marginRight: 'auto', width: '100%' }}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroBanner;
